/**
 * The Originals tab's pure parts (OriginalsView.jsx): the rows of
 * getOriginals (functions/src/services/originals.ts computeOriginals) put in
 * the screen's sections, and every code in plain words. Contract:
 * docs/ingest/19-capture-pc-originals.md.
 *
 * Sections:
 *   toDelete   safe to delete, and on the drive in the uploader's latest
 *              check of the known folder: the only folders a person is told
 *              to delete
 *   unchecked  safe to delete, but not seen on the drive in a check this
 *              screen trusts (no check yet, or the uploader reads another
 *              folder or drive now): not offered for deletion
 *   needs      a person looks first: held, or gone before it was safe (the
 *              last copy may be lost)
 *   untied     tied to no one (or tied, waiting to be relinked), on the drive
 *   keep       must stay for now, on the drive
 *   gone       not on the drive any more (in the latest check)
 */
import { SKIP_REASON_WORDS } from "./UploadStatusBar.jsx";

const byFolder = (a, b) => String(a.folder ?? "").localeCompare(String(b.folder ?? "")) || String(a.sn ?? "").localeCompare(String(b.sn ?? ""));

/** getOriginals' view in the screen's sections, each sorted by folder name. */
export function sectionsOf(view) {
  const out = { toDelete: [], unchecked: [], needs: [], untied: [], keep: [], gone: [] };
  if (!view) return out;
  const safe = (row) => (row.present === true ? out.toDelete : out.unchecked).push(row);
  for (const c of view.captures ?? []) {
    const row = { ...c, kind: "capture" };
    if (c.gone) {
      out.gone.push(row);
      if (c.lost) out.needs.push(row);
    // Held only because a label on the drive cannot be read (that folder is in Needs you): kept for now.
    } else if (c.status === "hold" && c.why === "label_doubt") out.keep.push(row);
    else if (c.status === "hold") out.needs.push(row);
    else if (c.status === "safe") safe(row);
    else out.keep.push(row);
  }
  for (const u of view.untied ?? []) {
    const row = { ...u, kind: "untied" };
    if (u.gone) {
      out.gone.push(row);
      if (u.lost) out.needs.push(row);
    } else if (u.status === "safe") safe(row);
    else if (u.status === "hold") out.needs.push(row);
    else out.untied.push(row);
  }
  for (const k of Object.keys(out)) out[k].sort(byFolder);
  return out;
}

/** The folder names still to delete, one per line (for "Copy the folder names"). */
export function folderListText(rows) {
  return rows.filter((r) => r.folder).map((r) => r.folder).join("\n");
}

// Flags only the inventory sets (the uploader's own skip reasons are in SKIP_REASON_WORDS).
const INVENTORY_FLAG_WORDS = { identity_unknown: "no dataset.json to check its label against" };
const flagWords = (flag) => SKIP_REASON_WORDS[flag] ?? INVENTORY_FLAG_WORDS[flag] ?? String(flag ?? "").replace(/_/g, " ");
// A label in doubt: the folder may be a guest's capture, so it is never listed to delete (whatever its dates).
const LABEL_DOUBT_WORDS = {
  manifest_unreadable: "Its label (manifest.json) cannot be read on the capture PC: whose capture this is cannot be told, and it may be a guest's. It is never listed to delete. Check the folder on the capture PC (can manifest.json be opened?).",
  manifest_invalid: "Its label (manifest.json) makes no sense (cut short or damaged): whose capture this is cannot be told, and it may be a guest's. It is never listed to delete. Check the folder on the capture PC.",
};

/** Why a row is where it is, in the crew's words. */
export function whyText(row) {
  const others = (row.alsoNamedBy ?? []).join(", ");
  switch (row.why) {
    case "files_online": return "Its files are online (the model approved, or photos only).";
    case "may_go": return "Its photos are in the cloud, and originals may go (the switch below).";
    case "discarded": return "Discarded, and its cloud copy is deleted.";
    case "delivery_deleted": return "Deleted in the Suite (the guest's own delete, a staff delete, left out, or its dates ran out).";
    case "download_over": return "The guest's download period is over.";
    case "not_guest": return "Not a guest's capture (decided here).";
    case "untied_30_days": return "Tied to no one 30 days after the event.";
    case "not_in_cloud": return "Its photos are not in the cloud yet.";
    case "waiting_model": return "Photos in the cloud; waiting for the model to be approved, or for photos only.";
    case "raw_deleting": return "A client's capture whose photos are being deleted: keep these originals until that is settled.";
    case "waiting_second_copy": return "A client's capture: its photos are in the archive; waiting for their copy in the client's cloud too.";
    case "discard_pending": return "Discarded; its cloud copy is still being deleted.";
    case "tied_waiting_relink": return "Tied to a registration; waiting to be relinked on the capture PC.";
    case "untied": return "Tied to no one yet.";
    case "flagged": return LABEL_DOUBT_WORDS[row.flag] ?? `The uploader skipped this folder (${flagWords(row.flag)}). Look at it first.`;
    case "duplicate_sn": return `Two folders name this capture${others ? ` (also ${others})` : ""}. Look at them first.`;
    case "moved": return others
      ? `Another folder is named for this capture too (${others}): the records or the drive disagree about where it is. Look first.`
      : "This folder's label changed since it was uploaded or decided. Look first.";
    case "shared_folder": return `Another capture's record names this same folder${others ? ` (${others})` : ""}, and that one cannot go yet. Look first.`;
    case "tied_elsewhere": return "Tied to a registration of another job. Look at that job first.";
    case "other_job_record": return "Another job's upload or capture names a folder of this name. Look at that job first.";
    case "label_removed": return "Its label named a capture once and was removed: not tied to no one. Look at it first.";
    case "label_history_full": return "Too many folders to remember their labels: no capture or folder is listed to delete or marked gone until Claude looks. Tell Claude.";
    case "folder_records_full": return "Too many captures named one folder: no capture is listed to delete or marked gone until Claude looks. Tell Claude.";
    case "tie_mismatch": return "This folder was tied to another registration than the capture it was relinked to: check which guest it is.";
    case "decision_changed": return "Declared not a guest's capture, but the folder on the drive is not the one declared (made again?): take the decision back and decide again.";
    case "label_doubt": return "A folder's label on the drive cannot be read, and it may be this capture's: kept until that folder is looked at.";
    case "folder_unknown": return "Its folder is not known. Find it by hand.";
    default: return "";
  }
}

/** The alarm on a row gone before it was safe. */
export function lostText(row) {
  if (row.kind !== "untied") return "Gone before its photos reached the cloud: the only copy may be lost.";
  if (row.decision?.kind === "tie") return "Gone before it was relinked: that guest's capture may be lost.";
  if (row.why === "tied_elsewhere") return "Gone while tied to a registration of another job: that guest's capture may be lost.";
  return "Gone before anyone decided whose it was, and before its 30 days: it may have been a guest's capture.";
}

const NY = "America/New_York";
/** "Sep 30, 8:15 PM" in New York. */
export function shortTime(ms) {
  return typeof ms === "number" && ms > 0
    ? new Date(ms).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: NY })
    : "";
}

/** "Oct 15" for a YYYY-MM-DD day (a calendar day, no time zone). */
export function dayText(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ""));
  if (!m) return "";
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/** "3 min ago" from now. */
export function agoText(ms, now) {
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 90) return `${s} s ago`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
}

/**
 * The recycle bin, in words: [text, tone]. unreadable: the other Windows
 * users' bins on that drive the uploader could not look into (only their
 * owner can): the empty it sees is only its own.
 */
export function recycleBinText(bin) {
  if (!bin) return ["Recycle Bin not checked.", "muted"];
  const others = typeof bin.unreadable === "number" && bin.unreadable > 0
    ? ` The uploader could not look into ${bin.unreadable} other Windows user${bin.unreadable === 1 ? "'s" : "s'"} Recycle Bin: if someone else deleted folders, empty theirs too.`
    : "";
  if (bin.state === "has_items") return [`The Recycle Bin on that drive holds ${bin.items} deleted item${bin.items === 1 ? "" : "s"}: empty it.${others}`, "needs"];
  if (bin.state === "empty") return [`The Recycle Bin on that drive is empty.${others}`, others ? "needs" : "live"];
  return ["The Recycle Bin could not be checked: empty it by hand.", "needs"];
}

/** A row's place on the drive, as a chip: [text, tone]. */
export function driveChip(row) {
  if (row.gone) return ["Gone", "live"];
  if (row.status === "hold") return ["Look first", "needs"];
  if (row.present === true) return row.status === "safe" ? ["Delete it", "needs"] : ["On the drive", "stopped"];
  return ["Not checked yet", "stopped"];
}

/**
 * "Use this folder": accept the place the uploader reads now, naming the very
 * check the admin is looking at (the Suite refuses it if a newer one came in).
 * null when there is no check to accept.
 */
/**
 * Whether the drive may be released for another job, as the tab sees it: a
 * recent check of the usual folder, held by this job, with every capture and
 * folder of it seen gone (the Suite checks it all again).
 */
export function canRelease(view) {
  const inv = view?.inventory;
  if (!inv || inv.stale || inv.rootMismatch || !inv.place?.mine || inv.complete !== true) return false;
  return [...(view.captures ?? []), ...(view.untied ?? [])].every((r) => r.gone);
}

/** The page's clocks now: the wall clock and the steady one (performance.now, which a clock change never moves). */
export function clockNow() {
  const mono = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  return { wall: Date.now(), mono };
}

/**
 * How long ago an answer came (`stamp`: clockNow() when it came), by the
 * larger of the two clocks: a wall clock set back, or a steady clock that
 * stood still while the computer slept, never makes an answer younger. No
 * stamp: as old as can be.
 */
export function elapsedSince(stamp, now = clockNow()) {
  if (!stamp) return Infinity;
  return Math.max(0, now.wall - stamp.wall, now.mono - stamp.mono);
}

/**
 * Load an answer, stamped with the page's clocks as they were when it was
 * asked for (not when it came): the time it took to come counts as its age
 * (agedView), so a slow answer never keeps its list to delete longer.
 */
export async function loadStamped(load, args, clock = clockNow) {
  const stamp = clock();
  const view = await load(args);
  return { view, stamp };
}

/**
 * getOriginals' view as it stands `elapsed` ms after it came: its "now"
 * moves on, and once its check is 30 minutes old (the Suite's freshForMs has
 * passed, or it gave none) the check is old here too: nothing is listed to
 * delete (no Delete it, no folder names to copy) and the drive cannot be
 * released, until a fresh answer comes. So a screen that cannot reach the
 * Suite, or a tab left open or asleep, never keeps an old list to delete.
 */
export function agedView(view, elapsed) {
  if (!view) return view;
  const moved = { ...view, now: (view.now ?? 0) + (Number.isFinite(elapsed) ? elapsed : 0) };
  const inv = view.inventory;
  if (!inv) return moved;
  if (!inv.stale && typeof inv.freshForMs === "number" && elapsed < inv.freshForMs) return moved;
  const unlist = (r) => (r.present === true ? { ...r, present: null } : r);
  return {
    ...moved,
    inventory: { ...inv, stale: true, freshForMs: 0, agedHere: !inv.stale },
    captures: (view.captures ?? []).map(unlist),
    untied: (view.untied ?? []).map(unlist),
  };
}

/** What staff saw of a folder tied to no one (its row), sent with a decision: refused if the drive says otherwise now. */
export function seenOf(row) {
  return { createdAt: row?.createdAt ?? null, photoCount: row?.photoCount ?? null };
}

/** "Release the drive": names the check looked at. null when there is none. */
export function releaseInput(jobId, view) {
  const scanId = view?.inventory?.scanId;
  return scanId ? { jobId, releasePlaceScanId: scanId } : null;
}

export function acceptRootInput(jobId, view) {
  const scanId = view?.inventory?.scanId;
  return scanId ? { jobId, acceptRootScanId: scanId } : null;
}

/**
 * Which answers of getOriginals the tab may show: only the newest load's,
 * and none while a change is being sent. A change makes every load started
 * before it stale, and no load starts until it is settled; then a fresh one
 * is the only one that counts.
 */
export function viewGate() {
  let seq = 0;
  let changing = 0;
  return {
    /** A new load's token, or null while a change is being sent (no load then). */
    startLoad() {
      if (changing > 0) return null;
      seq += 1;
      return seq;
    },
    /** May this load's answer be shown: the newest, and no change being sent. */
    accept(token) {
      return token !== null && token === seq && changing === 0;
    },
    isLatest(token) {
      return token === seq;
    },
    beginChange() {
      changing += 1;
      seq += 1;
    },
    endChange() {
      changing = Math.max(0, changing - 1);
    },
  };
}

/** The callables' refusals (details.reason, or the message), in words. */
export function actionErrorText(e) {
  const reason = e?.details?.reason ?? "";
  if (reason === "not_untied") return "That folder is no longer on the drive tied to no one (look again after the next check).";
  if (reason === "already_decided") return "That folder already has a decision. Take it back first.";
  if (reason === "decision_replaced") return "That folder was decided again meanwhile (in another tab?): look at it again.";
  if (reason === "folder_changed") return "That folder is not as you saw it any more (it was made again?): look at it again.";
  if (reason === "folder_facts_unknown") return "The uploader could not read that folder's creation time or photo count: tie it, or it goes from the 30th day.";
  if (reason === "entry_deleted") return "That registration was deleted. It cannot be tied to a capture.";
  if (reason === "no_entry") return "There is no such registration on this job.";
  if (reason === "root_changed") return "The uploader reads another folder or drive than before. Check that first.";
  if (reason === "no_inventory") return "The uploader has not reported the capture PC's folders yet.";
  if (reason === "scan_changed") return "A newer check of the capture PC came in meanwhile. Look at it again first.";
  if (reason === "tied_elsewhere") return "That folder is tied to a registration of another job. Look at that job first.";
  if (reason === "originals_on_drive") return "Some of this job's originals are not seen gone yet: the drive stays this job's.";
  if (reason === "not_releasable") return "Only a recent check of this job's usual folder, which this job holds, can release the drive.";
  if (/Requires role/.test(String(e?.message ?? ""))) return "Only an admin can do this.";
  return "That did not work. Try again.";
}

/**
 * The registrations a search finds for "Tie to a registration": by pass
 * number (exact, with or without #) or by name (every word found), at most
 * `max`, deleted registrations never.
 */
export function rosterMatches(roster, query, max = 8) {
  const q = String(query ?? "").trim().toLowerCase().replace(/^#/, "");
  if (!q) return [];
  const words = q.split(/\s+/).filter(Boolean);
  const out = [];
  for (const r of roster ?? []) {
    if (r?.deletion?.reason) continue;
    const num = String(r.displayId ?? "").toLowerCase().replace(/^#/, "");
    const name = String(r.name ?? "").toLowerCase();
    if ((num && num === q) || words.every((w) => name.includes(w))) out.push(r);
    if (out.length >= max) break;
  }
  return out;
}
